import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Table,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Input,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    Alert,
    ModalFooter,
    Label,
    FormGroup
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    FaMoneyBillWave,
    FaPlus,
    FaEdit,
    FaSearch,
    FaCheckCircle,
    FaClock,
    FaTimes,
    FaFileInvoice,
    FaTruck,
    FaFilter,
    FaInfoCircle,
    FaList,
    FaTh,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaImage,
    FaBarcode,
    FaBox,
    FaTag,
    FaBoxes,
    FaLink,
    FaTrashAlt
} from 'react-icons/fa';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice, roundToNearestInteger } from '../../helpers/utility';
import ReglementForm from './ReglementForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { clientActions } from '../../sagas/clientSlice';
import { debounce, set } from 'lodash';
import { deliveryActions } from '../../sagas/deliveryNoteSlice';
import { invoiceActions } from '../../sagas/invoiceSlice';
import Select from "react-select";
import { productActions } from '../../sagas/productSlice';
import { config } from '@fortawesome/fontawesome-svg-core';
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import { FaReceipt } from 'react-icons/fa';
import { soldeActions } from "../../sagas/soldeSlice";
import '../Common/DocumentList.css';
import DocumentPagination from '../Common/DocumentPagination';

const ReglementsList = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { loading, error, list } = useSelector(state => state.reglements);
    const { clients } = useSelector(state => state.clients);
    const [showModal, setShowModal] = useState(false);
    const [selectedReglement, setSelectedReglement] = useState(null);
    const [checkedReglements, setCheckedReglements] = useState([]); // State to track checked deliveries
    const [invoice, setInvoice] = useState(null); // State to hold the invoice object
    const [showInvoiceModal, setShowInvoiceModal] = useState(false); // State to control the invoice modal
    const [invoiceDateFacture, setInvoiceDateFacture] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' })); // Invoice date
    const [invoiceDateEcheance, setInvoiceDateEcheance] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' })); // Due date
    const { company } = useSelector(state => state.auth);

    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const location = useLocation();
    const [selectedClient, setSelectedClient] = useState(null);
    const searchInputRef = useRef(null);
    const [chooseProductModal, setChooseProductModal] = useState(false);
    const toggleChooseProductModal = () => setChooseProductModal(!chooseProductModal);
    const [modalSearchTerm, setModalSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unite
    const [displayMode, setDisplayMode] = useState('table');
    const [isSearching, setIsSearching] = useState(false);

    const {
        products,
        loading: productsLoading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error: productsError,
    } = useSelector((status) => status.products);

    const [filters, setFilters] = useState({
        clientId: null,
        chantierId: null,
        dateFrom: formatDate(todayDate),
	        dateTo: formatDate(tomorrowDate),
	        statut: null,
	        query: '',
	        modeReglement: '',
	        page: 0,
	        size: 10
    });

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [list]);

    useEffect(() => {
        if (!clients || clients.length === 0) {
            dispatch(clientActions.search({
                query: ""
            }));
        }
        dispatch(reglementActions.searchRequest(filters));

    }, [filters.clientId, filters.dateFrom, filters.dateTo, filters.modeReglement, filters.statut]);


    useEffect(() => {
        if (unites == null || unites.length === 0) {
            dispatch(productActions.findAllUnites());
        }

        dispatch(productActions.findAll({
            search: '',
            page: currentPage - 1,
            size: 10,
        }));

    }, [currentPage, chooseProductModal]);


    const handleShowModal = () => {
        navigate("/sales-module/payments/new");
    };

    const handleEdit = (reglement) => {
        navigate("/sales-module/payments/edit", { state: { reglement } });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReglement(null);
        handleReload(); // Refresh the filteredList instead of reloading the page
    };

    const handleReload = () => {
        dispatch(reglementActions.searchRequest(filters));
    };

    const debouncedSearch = () => {
        dispatch(reglementActions.searchRequest({
            ...filters,
        }));
    }

    const handleModalSearchChange = (e) => {
        const value = e.target.value;
        setModalSearchTerm(value);
        debouncedModalSearch(value);
    };

    // Parse dates "yyyy-MM-dd" (ISO court) ou "dd-MM-yyyy"
    const parseAnyDate = (v) => {
        if (!v) return null;
        if (typeof v === 'string') {
            // try ISO "yyyy-MM-dd"
            if (/^\d{4}-\d{2}-\d{2}/.test(v)) return new Date(v + 'T00:00:00');
            // try "dd-MM-yyyy"
            if (/^\d{2}-\d{2}-\d{4}$/.test(v)) {
                const [dd, mm, yyyy] = v.split('-').map(Number);
                return new Date(yyyy, mm - 1, dd);
            }
        }
        if (v instanceof Date) return v;
        return new Date(v);
    };

    const normalizeRef = (s) => (s || '').toString().trim().toUpperCase();

    const isSameRef = (a, b) => normalizeRef(a) === normalizeRef(b);

    // Détermine signe et montant (débit/crédit) pour un règlement
    // Ex: AVANCE / PAIEMENT => CREDIT (+), BL => DEBIT (-)
    const getReglementSignedAmount = (reglement) => {
        const montant = Number(reglement?.montantRegle || 0);
        const type = (reglement?.typeReglement || '').toString().toUpperCase();
        // Ajuste selon tes enums exacts si besoin
        if (type === 'AVANCE' || type === 'PAIEMENT' || type === 'REGLEMENT') {
            return { credit: montant, debit: 0 };
        }
        if (type === 'BON_LIVRAISON' || type === 'BL') {
            return { credit: 0, debit: montant };
        }
        // Par défaut: pas d’impact
        return { credit: 0, debit: 0 };
    };

    // Renvoie { soldeAvant, soldeApres } à partir d'une liste de mouvements déjà chargée
    const computeSoldeAvantApresFromMouvements = (reglement, mouvements = []) => {
        if (!mouvements || mouvements.length === 0) {
            return { soldeAvant: 0, soldeApres: 0 };
        }

        // 1) Essaie de trouver le mouvement du règlement via la référence
        const refReglement = normalizeRef(reglement?.reference || reglement?.id);
        const mvtReglement = mouvements.find(m =>
            isSameRef(m?.reference, refReglement)
        );
        if (mvtReglement) {
            return {
                soldeAvant: Number(mvtReglement.soldeAvant || 0),
                soldeApres: Number(mvtReglement.soldeApres || 0),
            };
        }

        // 2) Sinon, calcule via le dernier mouvement ≤ dateReglement
        const dReglement = parseAnyDate(reglement?.dateReglement);
        // On trie par date croissante; pour égalité, garde l’ordre d’origine
        const sorted = [...mouvements].sort((a, b) => {
            const da = parseAnyDate(a?.dateOperation || a?.date_mouvement);
            const db = parseAnyDate(b?.dateOperation || b?.date_mouvement);
            return da - db;
        });

        // Prend le dernier mouvement avant ou le même jour (si l’ordre n’est pas garanti intra-jour, on prend le dernier de la date)
        let lastBefore = null;
        for (const m of sorted) {
            const dm = parseAnyDate(m?.dateOperation || m?.date_mouvement);
            if (!dm || !dReglement) continue;
            if (dm.getTime() <= dReglement.getTime()) lastBefore = m;
        }

        // Base de départ : si aucun, 0
        const baseAvant = lastBefore ? Number(lastBefore.soldeApres || 0) : 0;

        // Applique l'impact du règlement pour estimer l'après
        const { credit, debit } = getReglementSignedAmount(reglement);
        // Convention: solde = crédits - débits cumulés
        const soldeApres = baseAvant + Number(credit || 0) - Number(debit || 0);

        return {
            soldeAvant: baseAvant,
            soldeApres,
        };
    };

    const getClientSoldeForReglement = (dispatch, reglement) => {
        return new Promise((resolve, reject) => {
            if (!reglement?.clientId) {
                resolve({ soldeAvant: 0, soldeApres: 0 });
                return;
            }

            dispatch(
                soldeActions.search({
                    clientId: reglement.clientId,
                    dateMouvementTo: reglement.dateReglement, // <= inclusif jusqu’à cette date
                    typeDocument: "",                          // filtre libre
                    onSuccess: (rows) => {
                        try {
                            const { soldeAvant, soldeApres } = computeSoldeAvantApresFromMouvements(reglement, rows || []);
                            resolve({ soldeAvant, soldeApres });
                        } catch (e) {
                            resolve({ soldeAvant: 0, soldeApres: 0 });
                        }
                    },
                    onError: (err) => {
                        resolve({ soldeAvant: 0, soldeApres: 0 });
                    },
                })
            );
        });
    };


    const handleGenerateReceipt = async (reglement) => {
        try {
            // 1) Solde courant depuis Redux
            const { soldeAvant, soldeApres } = await getClientSoldeForReglement(dispatch, reglement);

            // 2) (Optionnel) calculer un solde “après encaissement” si tu veux l’afficher
            // const montantEncaisse = reglement?.statut === 'ENCAISSE' ? Number(reglement.montantRegle || 0) : 0;
            // const soldeApres = solde - montantEncaisse;

            // 3) Générer le PDF
            const doc = buildPaymentReceiptDoc(
                reglement,
                soldeAvant,
                soldeApres,
                t,
                {
                    showBeforeAfter: true,
                    soldeApres: soldeApres,
                    soldeAvant: soldeAvant
                }
            );

            pdfMake.createPdf(doc).open(); // .download(`Recu_${reglement.reference || reglement.id}.pdf`)
        } catch (e) {
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: t('reglement.errorGenerateReceipt') || 'Erreur lors de la génération du reçu.',
            });
        }
    };

    const handleGoLettrage = (reglement) => {
        // On ne lettrer que les AVANCES
        if (!reglement.estAvance && reglement.typeReglement !== 'AVANCE') return;

        const reste = Number(reglement.montantRegle || 0) - Number(reglement.montantAffecte || 0);
        if (reste <= 0) {
            Swal.fire({
                icon: 'info',
                title: t('common.information'),
                text: t('reglement.noAmountLeftToAllocate') || "Aucun montant restant à allouer sur cette avance.",
            });
            return;
        }
        navigate(`/sales-module/reglements/${reglement.id}/lettrage`);
    };


    const buildPaymentReceiptDoc = (reglement, soldeAvant, soldeApres, t, opts = {}) => {
        const {
            devise = 'MAD',
            showBeforeAfter = true,          // on l’active par défaut
            footerNote = null,
        } = opts || {};

        const primary = '#06912b';
        const ink = '#0F172A';
        const line = '#E2E8F0';
        const subtle = '#64748B';

        const safe = (v, fb = '-') => (v || v === 0 ? v : fb);

        const formatDateFR = (isoOrDate) => {
            if (!isoOrDate) return '-';
            try {
                const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
                return d.toLocaleDateString('fr-FR');
            } catch {
                return '-';
            }
        };

        const formatMoney = (n) => {
            const x = Number(n || 0);
            const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
            return `${s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ${devise}`;
        };

        const paymentStatusLabel = (r) =>
            r?.statut ? (t(`reglement.${r.statut.toLowerCase()}`) || r.statut) : t('reglement.enAttente');

        /** --- Informations entreprise (suppose "company" dispo dans le scope) --- **/
        const companyName = safe(company.name, 'Entreprise');
        const companySubtitle = safe(company.subtitle, '');
        const companyTel = [company.phone1, company.phone2].filter(Boolean).join(' / ');
        const companyEmail = safe(company.email, '');
        const companyAddr = safe(company.address, '');
        const companyBank = safe(company.bankName, '');
        const companyRib = safe(company.RIB, '');
        const companyICE = safe(company.ice, '');
        const companyIF = safe(company.identificationFiscale, '');
        const companyRC = safe(company.rc, '');
        const companyPatente = safe(company.patente, '');
        const companyCNSS = safe(company.cnss, '');
        const logoPath = company.logo ? { image: company.logo, width: 70 } : null;

        /** --- Tableau des infos du règlement --- **/
        const metaRows = [
            [{ text: t('reglement.reference'), bold: true }, safe(reglement.reference || String(reglement.id))],
            [{ text: t('reglement.client'), bold: true }, safe(reglement.nomClient)],
            [{ text: t('reglement.chantier'), bold: true }, safe(reglement.nomChantier)],
            [{ text: t('reglement.dateReglement'), bold: true }, formatDateFR(reglement.dateReglement)],
            [{ text: t('reglement.modeReglement'), bold: true }, t(`reglement.${(reglement.modeReglement || 'AUTRE').toLowerCase()}`)],
            [{ text: t('reglement.montantRegle'), bold: true }, formatMoney(reglement.montantRegle)],
            [{ text: t('reglement.statut'), bold: true }, paymentStatusLabel(reglement)],
        ];

        const etatTxt = `${t('reglement.paymentState') || 'État du paiement'}: ${paymentStatusLabel(reglement)}`;

        /** --- Documents liés --- **/
        const hasDocs = reglement?.numFactures || reglement?.numBonLivraisons;
        const docsBlock = hasDocs
            ? {
                table: {
                    widths: ['*'],
                    body: [
                        [{ text: t('reglement.documents') || 'Documents liés', bold: true, color: primary }],
                        [
                            {
                                stack: [
                                    reglement.numFactures
                                        ? { text: `${t('reglement.factures')}: ${reglement.numFactures}`, fontSize: 10 }
                                        : null,
                                    reglement.numBonLivraisons
                                        ? { text: `${t('reglement.bonLivraisons')}: ${reglement.numBonLivraisons}`, fontSize: 10 }
                                        : null,
                                ].filter(Boolean),
                            },
                        ],
                    ],
                },
                layout: {
                    hLineColor: () => line,
                    vLineColor: () => line,
                    paddingLeft: () => 8,
                    paddingRight: () => 8,
                    paddingTop: () => 6,
                    paddingBottom: () => 6,
                },
                margin: [0, 0, 0, 12],
            }
            : null;

        /** --- Bloc Solde Avant / Montant Réglé / Solde Après --- **/
        const beforeAfterBlock =
            showBeforeAfter
                ? {
                    table: {
                        widths: ['33%', '34%', '33%'],
                        body: [
                            [
                                { text: 'Solde avant', style: 'th' },
                                { text: 'Montant réglé', style: 'th' },
                                { text: 'Solde après', style: 'th' },
                            ],
                            [
                                { text: formatMoney(soldeAvant), alignment: 'center' },
                                { text: formatMoney(reglement.montantRegle), alignment: 'center' },
                                { text: formatMoney(soldeApres), alignment: 'center', bold: true },
                            ],
                        ],
                    },
                    layout: {
                        fillColor: (rowIndex) => (rowIndex === 0 ? '#F8FAFC' : null),
                        hLineColor: () => line,
                        vLineColor: () => line,
                        paddingLeft: () => 8,
                        paddingRight: () => 8,
                        paddingTop: () => 6,
                        paddingBottom: () => 6,
                    },
                    margin: [0, 6, 0, 12],
                }
                : null;

        const politeNote =
            footerNote ||
            t('reglement.reminderLine') ||
            'Merci pour votre règlement. En cas de solde restant, veuillez régulariser dans les meilleurs délais.';

        return {
            pageSize: 'A4',
            pageMargins: [32, 36, 32, 36],
            info: {
                title: `Reçu - ${safe(reglement.reference || reglement.id)}`,
                author: companyName,
                subject: 'Reçu de paiement',
            },
            content: [
                /** --- En-tête --- **/
                {
                    columns: [
                        {
                            stack: [
                                { text: companyName, fontSize: 14, bold: true, color: ink, margin: [0, 0, 0, 2] },
                                companySubtitle ? { text: companySubtitle, fontSize: 10, color: subtle, margin: [0, 0, 0, 4] } : null,
                                { text: companyAddr, fontSize: 9, color: subtle },
                                companyTel ? { text: `Tél: ${companyTel}`, fontSize: 9, color: subtle } : null,
                                companyEmail ? { text: `Email: ${companyEmail}`, fontSize: 9, color: subtle } : null,
                            ].filter(Boolean),
                        },
                        {
                            alignment: 'right',
                            stack: [
                                { text: (t('reglement.receiptTitle') || 'Reçu de Paiement'), fontSize: 16, bold: true, color: primary, margin: [0, 4, 0, 2] },
                                { text: `# ${safe(reglement.reference || reglement.id, '-')}`, fontSize: 10, color: subtle },
                                { text: formatDateFR(new Date()), fontSize: 9, color: subtle },
                            ].filter(Boolean),
                        },
                    ],
                    margin: [0, 0, 0, 12],
                },

                /** --- Infos règlement --- **/
                {
                    table: { widths: ['35%', '*'], body: metaRows },
                    layout: {
                        fillColor: () => '#FFFFFF',
                        hLineColor: () => line,
                        vLineColor: () => line,
                        paddingLeft: () => 8,
                        paddingRight: () => 8,
                        paddingTop: () => 6,
                        paddingBottom: () => 6,
                    },
                    margin: [0, 0, 0, 12],
                },

                /** --- État + Solde --- **/
                {
                    stack: [
                        { text: etatTxt, bold: true, color: ink, margin: [0, 2, 0, 2] },
                    ],
                    margin: [0, 4, 0, 6],
                },

                ...(beforeAfterBlock ? [beforeAfterBlock] : []),
                ...(docsBlock ? [docsBlock] : []),

                /** --- Informations fiscales et bancaires --- **/
                {
                    table: {
                        widths: ['50%', '50%'],
                        body: [
                            [
                                {
                                    stack: [
                                        companyIF ? { text: `I.F: ${companyIF}`, fontSize: 9 } : null,
                                        companyICE ? { text: `ICE: ${companyICE}`, fontSize: 9 } : null,
                                        companyRC ? { text: `RC: ${companyRC}`, fontSize: 9 } : null,
                                        companyPatente ? { text: `Patente: ${companyPatente}`, fontSize: 9 } : null,
                                        companyCNSS ? { text: `CNSS: ${companyCNSS}`, fontSize: 9 } : null,
                                    ].filter(Boolean),
                                },
                                {
                                    stack: [
                                        companyBank ? { text: `Banque: ${companyBank}`, fontSize: 9 } : null,
                                        companyRib ? { text: `RIB: ${companyRib}`, fontSize: 9 } : null,
                                    ].filter(Boolean),
                                },
                            ],
                        ],
                    },
                    layout: {
                        hLineColor: () => line,
                        vLineColor: () => line,
                        paddingLeft: () => 8,
                        paddingRight: () => 8,
                        paddingTop: () => 4,
                        paddingBottom: () => 4,
                    },
                    margin: [0, 6, 0, 12],
                },

                /** --- Note finale --- **/
                {
                    text: politeNote,
                    italics: true,
                    fontSize: 10,
                    color: subtle,
                },
            ],
            footer: (currentPage, pageCount) => ({
                columns: [
                    { text: companyName, alignment: 'left', color: subtle, fontSize: 8, margin: [32, 0, 0, 0] },
                    { text: `${currentPage} / ${pageCount}`, alignment: 'right', color: subtle, fontSize: 8, margin: [0, 0, 32, 0] },
                ],
                margin: [0, 8],
            }),
            styles: {
                th: { bold: true, color: ink, alignment: 'center' },
            },
            defaultStyle: { fontSize: 10 },
        };
    };



    const debouncedModalSearch = useCallback(
        debounce((query) => {
            setIsSearching(true);
            dispatch(productActions.findAll({
                search: query,
                page: 0,
                size: pageSize,
                onSuccess: () => setIsSearching(false)
            }));
            setIsSearching(false);
        }, 1000), // Wait 1 second after user stops typing
        [modalSearchTerm]
    );

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const TableView = () => (
        <>
            <div className="cart-container" style={{
                height: '300px',
                overflowY: 'auto'
            }}>

                <Table hover bordered className="align-middle mb-0">
                    <thead className="bg-light">
                        <tr>
                            <th className="align-middle">
                                <FaBarcode className="me-2 text-primary" size={12} />
                                {t('product.code')}
                            </th>
                            <th className="align-middle">
                                <FaBox className="me-2 text-primary" size={12} />
                                {t('product.description')}
                            </th>
                            <th className="align-middle text-end">
                                <FaTag className="me-2 text-primary" size={12} />
                                {t('product.salePrice')}
                            </th>
                            {/* <th className="align-middle text-end">
                                <FaTag className="me-2 text-primary" size={12} />
                                {t('product.buyingPrice')}
                            </th> */}
                            <th className="align-middle text-center">
                                <FaBoxes className="me-2 text-primary" size={12} />
                                {t('product.stockPhysique')}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {!products || products.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-4">
                                    <div className="text-muted">
                                        {t('product.noProducts')}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            products.map((product) => (
                                <tr key={product.id} onClick={() => addInvoiceLine(product)}>
                                    <td className="align-middle">{product.code}</td>
                                    <td className="align-middle">
                                        {product.libelle.length > 30 ? product.libelle.substring(0, 30) + '...' : product.libelle}
                                    </td>
                                    <td className="align-middle text-end">
                                        {formatPrice(product.prixVenteTTC1)}
                                    </td>
                                    {/* <td className="align-middle text-end">{formatPrice(product.prixAchatTTC)}</td> */}
                                    <td className="align-middle text-center">{product.stockPhysique}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                    {t('product.showing', {
                        start: (currentPage - 1) * pageSize + 1,
                        end: Math.min(currentPage * pageSize, totalElements),
                        total: totalElements,
                        currentPage: currentPage,
                        totalPages: totalPages
                    })}
                </small>
            </div>
        </>
    );

    const CardView = () => (
        <div className="card-container" style={{ height: '400px', overflowY: 'auto' }}>
            <Row xs={2} md={3} lg={3} className="g-3">
                {(!products || products.length === 0) && (
                    <Col xs={12} className="text-center py-4 ">
                        {t('product.noProducts')}
                    </Col>
                )}
                {products.map(product => (
                    <Col key={product.id}>
                        <Card className="product-card rounded border shadow-sm" onClick={() => addInvoiceLine(product)}>
                            <div className="card-image-container">
                                {product.photo ? (
                                    <img
                                        src={`${config.baseURL}${product.photo}`}
                                        alt={product.libelle}
                                        className="card-img-top rounded-top"
                                    />
                                ) : (
                                    <div className="bg-placeholder rounded-top d-flex align-items-center justify-content-center">
                                        <FaImage size={32} className="text-muted" />
                                    </div>
                                )}
                            </div>
                            <CardBody className="d-flex flex-column">
                                <div className="d-flex justify-content-between align-items-start">
                                    <h6 className="product-title mb-2">
                                        {product.libelle.length > 30 ? product.libelle.substring(0, 30) + '...' : product.libelle}
                                    </h6>
                                </div>
                                {product.unite ? `${formatPrice(product.prixVenteTTC1)} / ${product.unite}` : 'N/A'}
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );

    const DisplayToggle = () => (
        <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="btn-group">
                <Button
                    color={displayMode === 'table' ? 'primary' : 'light'}
                    onClick={() => setDisplayMode('table')}
                    size="sm"
                >
                    <FaList className="me-1" /> {t('common.table')}
                </Button>
                <Button
                    color={displayMode === 'card' ? 'primary' : 'light'}
                    onClick={() => setDisplayMode('card')}
                    size="sm"
                >
                    <FaTh className="me-1" /> {t('common.cards')}
                </Button>
            </div>

            <div className="d-flex align-items-center gap-3">
                <small className="text-muted">
                    {t('product.showing', {
                        start: (currentPage - 1) * pageSize + 1,
                        end: Math.min(currentPage * pageSize, totalElements),
                        total: totalElements,
                        currentPage: currentPage,
                        totalPages: totalPages
                    })}
                </small>

                <div className="d-flex align-items-center gap-2">
                    <Button color="light" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                        <FaAngleDoubleLeft />
                    </Button>
                    <Button color="light" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        <FaAngleLeft />
                    </Button>

                    <span className="mx-2">
                        {currentPage} / {totalPages}
                    </span>

                    <Button color="light" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        <FaAngleRight />
                    </Button>
                    <Button color="light" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
                        <FaAngleDoubleRight />
                    </Button>
                </div>
            </div>
        </div>
    );

    const handleAnnulerReglement = (reglement) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmAnnulation'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementActions.annulerReglement({
                    id: reglement.id,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.annulationSuccess'),
                        });
                        handleReload(); // Refresh the filteredList
                    }
                }));
            }
        });
    }

    const handleEncaisserReglement = (reglement) => {
        const isDocumentPayment = reglement.typeReglement === 'FACTURE' || reglement.typeReglement === 'BON_LIVRAISON';
        const hasSelectedDocuments = Boolean(reglement.numFactures) || Boolean(reglement.numBonLivraisons);

        if (isDocumentPayment && !hasSelectedDocuments) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning', 'Attention'),
                text: t('reglement.documentsRequiredBeforeEncaisser', 'Veuillez éditer le règlement et sélectionner les documents à régler avant l’encaissement.'),
            });
            return;
        }

        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmEncaissement'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementActions.encaisserReglement({
                    id: reglement.id,
                    dateEncaissement: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.encaissementSuccess'),
                        });

                        handleReload(); // Refresh the filteredList

                    }
                }));
            }
        });
    };

    const clientOptions = clients?.map(client => ({
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers || []
    })) ?? [];

    const clientOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...clientOptions
    ];

    const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
        value: chantier.id,
        label: `${chantier.code} - ${chantier.nom}`,
        ...chantier
    })) || [];

    const chantierOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...chantierOptions
    ];


    const getStatutBadge = (statut) => {
        const badges = {
            'EN_ATTENTE': { color: 'info', icon: FaClock },
            'ENCAISSE': { color: 'success', icon: FaCheckCircle },
            'IMPAYE': { color: 'danger', icon: FaTimes },
            'ANNULE': { color: 'danger', icon: FaTimes }
        };

        const badge = badges[statut] || { color: 'secondary', icon: FaClock };
        const Icon = badge.icon;

        return (
            <Badge color={badge.color}>
                <Icon className="me-1" size="12" />
                {t(`reglement.${statut.toLowerCase()}`)}
            </Badge>
        );
    };

    const getModeReglementIcon = (mode) => {
        const icons = {
            'ESPECES': '💵',
            'CHEQUE': '📝',
            'VIREMENT': '🏦',
            'CARTE_BANCAIRE': '💳',
            'TRAITE': '📄',
            'AUTRE': '❓'
        };
        return icons[mode] || '❓';
    };

    const clearFilters = () => {
        setFilters({
            query: '',
            statut: '',
	            modeReglement: '',
	            clientId: null,
	            chantierId: null,
	            dateFrom: formatDate(todayDate),
	            dateTo: formatDate(tomorrowDate),
	            page: 0,
	            size: filters.size
	        });
    };

    const handleCheckboxChange = (reglementId) => {
        setCheckedReglements((prevChecked) => {
            if (prevChecked.includes(reglementId)) {
                return prevChecked.filter((id) => id !== reglementId);
            } else {
                return [...prevChecked, reglementId];
            }
        });
    };

    // Helper function to calculate line totals
    const calculateLineTotals = (ligne) => {
        const quantite = parseFloat(ligne.quantite) || 0;
        const prixUnitaireTTC = parseFloat(ligne.prixUnitaireTTC) || 0;
        const tauxTVA = parseFloat(ligne.tauxTVA) || 0;

        const montantHT = Math.round((quantite * (prixUnitaireTTC / (1 + tauxTVA))) * 100) / 100;
        const montantTVA = Math.round((montantHT * tauxTVA) * 100) / 100;
        const montantTTC = Math.round((montantHT + montantTVA) * 100) / 100;

        return {
            ...ligne,
            quantite,
            prixUnitaireTTC,

            tauxTVA,
            montantHT,
            montantTVA,
            montantTTC
        };
    };

    // Helper function to recalculate invoice totals
    const recalculateInvoiceTotals = (lignes) => {
        const montantTotalHT = lignes.reduce((sum, ligne) => sum + (ligne.montantHT || 0), 0);
        const montantTotalTVA = lignes.reduce((sum, ligne) => sum + (ligne.montantTVA || 0), 0);
        const montantTotalTTC = lignes.reduce((sum, ligne) => sum + (ligne.montantTTC || 0), 0);

        return {
            montantTotalHT,
            montantTotalTaxe: montantTotalTVA,
            montantTotalTTC
        };
    };

    // Function to update invoice line
    const updateInvoiceLine = (index, field, value) => {
        setInvoice(prev => {
            const updatedLignes = [...prev.lignes];
            updatedLignes[index] = {
                ...updatedLignes[index],
                [field]: value
            };

            // Recalculate line totals
            updatedLignes[index] = calculateLineTotals(updatedLignes[index]);

            // Recalculate invoice totals
            const totals = recalculateInvoiceTotals(updatedLignes);

            return {
                ...prev,
                lignes: updatedLignes,
                ...totals
            };
        });
    };

    // Function to add new line
    const addInvoiceLine = (product) => {
        setInvoice(prev => {
            const newLine = {
                articleId: product.id,
                articleCode: product.code,
                articleNom: product.libelle,
                unite: unites.find(u => u.code === product.unite)?.libelle || '',
                prixUnitaireHT: roundToNearestInteger(product.prixVenteTTC1 / (1 + (product.tauxTVAVente || 0.2))),
                prixUnitaireTTC: roundToNearestInteger(product.prixVenteTTC1),
                tauxTVA: product.tauxTVAVente || 0.2,
                quantite: 1,
                montantHT: roundToNearestInteger(product.prixVenteTTC1 / (1 + (product.tauxTVAVente || 0.2))),
                montantTVA: roundToNearestInteger(product.prixVenteTTC1 - (product.prixVenteTTC1 / (1 + (product.tauxTVAVente || 0.2)))),
                montantTTC: roundToNearestInteger(product.prixVenteTTC1),
                isAdjustment: true
            };

            const updatedLignes = [...prev.lignes, calculateLineTotals(newLine)];
            const totals = recalculateInvoiceTotals(updatedLignes);

            return {
                ...prev,
                lignes: updatedLignes,
                ...totals
            };
        });

        toggleChooseProductModal();
    };

    // Function to remove line
    const removeInvoiceLine = (index) => {
        setInvoice(prev => {
            const updatedLignes = prev.lignes.filter((_, i) => i !== index);
            const totals = recalculateInvoiceTotals(updatedLignes);

            return {
                ...prev,
                lignes: updatedLignes,
                ...totals
            };
        });
    };

    // Handle creating an invoice from selected reglements
    const handleCreateInvoice = async () => {
        if (checkedReglements.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reglement.noReglementsSelected'),
            });
            return;
        }

        // Get the selected reglements
        const selectedReglements = safeList.filter(reglement => checkedReglements.includes(reglement.id));

        // Check if all selected reglements are from the same client and are valid types
        const firstClient = selectedReglements[0].clientId;
        const allSameClient = selectedReglements.every(reg => reg.clientId === firstClient);
        const allValidTypes = selectedReglements.every(reg => ['AVANCE', 'BON_LIVRAISON'].includes(reg.typeReglement));

        if (!allSameClient) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reglement.notesFromDifferentClients'),
            });
            return;
        }

        if (!allValidTypes) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reglement.invalidReglementTypes'),
            });
            return;
        }

        try {
            // Show loading
            Swal.fire({
                title: t('common.loading'),
                text: t('reglement.fetchingBonLivraisons'),
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            let allBonLivraisons = [];

            // For BON_LIVRAISON type reglements, fetch the actual BLs
            for (const reglement of selectedReglements) {
                if (reglement.typeReglement === 'BON_LIVRAISON' && reglement.numBonLivraisons) {
                    const blRefs = reglement.numBonLivraisons.split(',').map(ref => ref.trim());

                    // Fetch each BL
                    for (const blRef of blRefs) {
                        try {
                            await new Promise((resolve, reject) => {
                                dispatch(deliveryActions.findByReference({
                                    reference: blRef,
                                    onSuccess: (data) => {
                                        if (data) {
                                            console.log(`Fetched BL ${blRef}:`, data);
                                            allBonLivraisons.push(data);
                                        }
                                        resolve();
                                    },
                                    onError: (error) => {
                                        console.error(`Error fetching BL ${blRef}:`, error);
                                        resolve(); // Continue with other BLs even if one fails
                                    }
                                }));
                            });
                        } catch (error) {
                            console.error(`Error fetching BL ${blRef}:`, error);
                        }
                    }
                }
            }

            Swal.close();

            const totalReglements = selectedReglements.reduce((total, reglement) => total + (reglement.montantRegle || 0), 0);

            // Process lines from BLs
            let processedLines = [];
            if (allBonLivraisons.length > 0) {
                const lineMap = {};
                allBonLivraisons.forEach(bl => {
                    if (bl.lignes) {
                        bl.lignes.forEach(line => {
                            const key = line.articleId;
                            if (!lineMap[key]) {
                                lineMap[key] = {
                                    articleId: line.articleId,
                                    articleCode: line.articleCode,
                                    articleNom: line.articleNom,
                                    unite: line.unite,
                                    prixUnitaireHT: line.prixUnitaireHT,
                                    prixUnitaireTTC: line.prixUnitaireTTC,
                                    tauxTVA: line.tauxTVA,
                                    quantite: 0,
                                    montantHT: 0,
                                    montantTVA: 0,
                                    montantTTC: 0,
                                };
                            }

                            lineMap[key].quantite += line.quantiteLivree || line.quantite;
                            lineMap[key].montantHT += (line.quantiteLivree || line.quantite) * line.prixUnitaireHT;
                            lineMap[key].montantTVA += (line.quantiteLivree || line.quantite) * line.prixUnitaireHT * line.tauxTVA;
                            lineMap[key].montantTTC += (line.quantiteLivree || line.quantite) * line.prixUnitaireHT * (1 + line.tauxTVA);
                        });
                    }
                });
                processedLines = Object.values(lineMap);
            }


            // Prepare the invoice object
            const newInvoice = {
                clientId: selectedReglements[0].clientId,
                clientNom: selectedReglements[0].nomClient,
                selectedReglements: selectedReglements.map(reg => ({
                    id: reg.id,
                    reference: reg.reference || reg.id,
                    dateReglement: reg.dateReglement,
                    montantRegle: reg.montantRegle,
                    typeReglement: reg.typeReglement,
                    modeReglement: reg.modeReglement
                })),
                lignes: processedLines,
                targetMontantTTC: totalReglements, // Store the target amount
                montantTotalTTC: totalReglements,
                montantTotalHT: totalReglements / 1.20, // Assuming 20% VAT
                montantTotalTaxe: totalReglements - (totalReglements / 1.20),
                montantTotalPaye: totalReglements,
                montantRegle: totalReglements,
                modePaiement: selectedReglements[0].modePaiement,
                modeReglement: selectedReglements[0].modeReglement,
                dateFacture: invoiceDateFacture,
                dateEcheance: invoiceDateEcheance,
                // Reference the original reglements
                reglementsIds: selectedReglements.map(r => r.id),
                bonLivraisonRefs: allBonLivraisons.map(bl => bl.numero).join(','),
                fromPayments: true, // Indicate that this invoice is created from payments
                paymentsRefs: selectedReglements.map(r => r.reference || r.id).join(','),
            };

            // Initialize date states with current invoice dates
            setInvoiceDateFacture(newInvoice.dateFacture);
            setInvoiceDateEcheance(newInvoice.dateEcheance);

            setInvoice(newInvoice);
            setShowInvoiceModal(true);

        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: t('reglement.errorFetchingBonLivraisons'),
            });
            console.error('Error creating invoice:', error);
        }
    };


    // Handle closing invoice modal
    const handleCloseInvoiceModal = () => {
        setShowInvoiceModal(false);
        setInvoice(null);
        // Reset date states
        setInvoiceDateFacture(new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }));
        setInvoiceDateEcheance(new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }));
    };

    const submitInvoice = () => {
        // Validation: ensure invoice has lines
        if (!invoice.lignes || invoice.lignes.length === 0) {
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: t('reglement.invoiceMustHaveLines'),
            });
            return;
        }

        // Validation: ensure totals match
        const calculatedTotal = invoice.lignes.reduce((sum, ligne) => sum + (ligne.montantTTC || 0), 0);
        const targetTotal = invoice.targetMontantTTC || invoice.montantTotalTTC;

        if (Math.abs(calculatedTotal - targetTotal) > 0.01) { // Allow small rounding differences
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: t('reglement.totalsMustMatch'),
            });
            return;
        }

        const invoiceData = {
            ...invoice,
            statut: 'PAYEE',
        };

        let payload = {
            invoice: invoiceData,
            onSuccess: (savedInvoice) => {
                Swal.fire({
                    icon: 'success',
                    title: t('common.success'),
                    text: t('reglement.invoiceCreatedSuccess'),
                });
                handleCloseInvoiceModal();
                setCheckedReglements([]);
                handleReload();
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('facture.invoiceSaveError'),
                    text: t('facture.invoiceSaveErrorMessage', { error }),
                });
            },
        };

        if (invoice.id) {
            dispatch(invoiceActions.updateInvoice(payload));
        } else {
            dispatch(invoiceActions.addInvoice(payload));
        }
    };

    // Handle confirming invoice creation
    const handleConfirmCreateInvoice = () => {
        if (!invoice) return;

        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmCreateInvoiceText'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                submitInvoice();
            }
        });
    };

    // Calculate statistics
    const safeList = Array.isArray(list) ? list.filter(Boolean) : [];
    const totalReglements = !loading ? safeList.length : 0;
    const totalMontant = !loading ? safeList.filter(r => r.typeReglement !== 'CAUTION').reduce((sum, r) => sum + (r?.montantRegle || 0), 0) : 0;
    const reglementsEnAttente = !loading ? safeList.filter(r => r.statut === 'EN_ATTENTE' && r.typeReglement !== 'CAUTION').length : 0;
    const totalMontantEnAttente = !loading ? safeList.filter(r => r.statut === 'EN_ATTENTE' && r.typeReglement !== 'CAUTION').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
    const reglementsEncaisses = !loading ? safeList.filter(r => r.statut === 'ENCAISSE' && r.typeReglement !== 'CAUTION').length : 0;
	    const totalMontantEncaisses = !loading ? safeList.filter(r => r.statut === 'ENCAISSE' && r.typeReglement !== 'CAUTION').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
	    const totalCaution = !loading ? safeList.filter(r => r.typeReglement === 'CAUTION').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
	    const reglementsCaution = !loading ? safeList.filter(r => r.typeReglement === 'CAUTION').length : 0;
	    const reglementPageNumber = Number(filters.page || 0);
	    const reglementPageSize = Number(filters.size || 10);
	    const paginatedSafeList = safeList.slice(
	        reglementPageNumber * reglementPageSize,
	        reglementPageNumber * reglementPageSize + reglementPageSize
	    );
	    const reglementTotalPages = Math.ceil(safeList.length / reglementPageSize);
	    const navigate = useNavigate();

    return (
        <Container fluid>
            <Row className="mb-2">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaMoneyBillWave className="text-primary me-2" />
                                {t('reglement.list')}
                            </h4>
                            <div className="d-flex align-items-center gap-2">
                                <Button color="primary" onClick={handleShowModal}>
                                    <FaPlus className="me-2" />
                                    {t('reglement.new')}
                                </Button>
                                {checkedReglements.length > 0 && (
                                    <Button
                                        color="success"
                                        onClick={handleCreateInvoice}
                                        className="d-flex align-items-center"
                                    >
                                        <FaFileInvoice className="me-2" />
                                        {t('reglement.createInvoice')} ({checkedReglements.length})
                                    </Button>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Statistics Cards */}
            <Row>
                <Col lg={3} md={6} sm={6} xs={12} className="mb-2">
                    <Card className="shadow-sm h-100">
                        <CardBody className="p-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-uppercase text-primary fw-bold" style={{ fontSize: '11px' }}>
                                        {t('reglement.totalReglements')}
                                    </div>
                                    <div className="fw-bold" style={{ fontSize: '18px' }}>
                                        {totalReglements}
                                    </div>
                                    <div className="text-primary" style={{ fontSize: '11px' }}>
                                        {formatPrice(totalMontant)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-primary bg-opacity-10 p-2">
                                    <FaMoneyBillWave size={18} className="text-primary" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col lg={3} md={6} sm={6} xs={12} className="mb-2">
                    <Card className="shadow-sm h-100">
                        <CardBody className="p-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-uppercase text-warning fw-bold" style={{ fontSize: '11px' }}>
                                        {t('reglement.enAttente')}
                                    </div>
                                    <div className="fw-bold" style={{ fontSize: '18px' }}>
                                        {reglementsEnAttente}
                                    </div>
                                    <div className="text-warning" style={{ fontSize: '11px' }}>
                                        {formatPrice(totalMontantEnAttente)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-warning bg-opacity-10 p-2">
                                    <FaClock size={18} className="text-warning" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col lg={3} md={6} sm={6} xs={12} className="mb-2">
                    <Card className="shadow-sm h-100">
                        <CardBody className="p-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-uppercase text-success fw-bold" style={{ fontSize: '11px' }}>
                                        {t('reglement.encaisses')}
                                    </div>
                                    <div className="fw-bold" style={{ fontSize: '18px' }}>
                                        {reglementsEncaisses}
                                    </div>
                                    <div className="text-success" style={{ fontSize: '11px' }}>
                                        {formatPrice(totalMontantEncaisses)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-success bg-opacity-10 p-2">
                                    <FaCheckCircle size={18} className="text-success" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col lg={3} md={6} sm={6} xs={12} className="mb-2">
                    <Card className="shadow-sm h-100">
                        <CardBody className="p-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-uppercase text-info fw-bold" style={{ fontSize: '11px' }}>
                                        {t('reglement.caution')}
                                    </div>
                                    <div className="fw-bold" style={{ fontSize: '18px' }}>
                                        {reglementsCaution}
                                    </div>
                                    <div className="text-info" style={{ fontSize: '11px' }}>
                                        {formatPrice(totalCaution)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-info bg-opacity-10 p-2">
                                    <FaMoneyBillWave size={18} className="text-info" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>




            {/* Filters */}
            <Card className="shadow-sm">
                <CardBody>
	                    <Row className="g-2 align-items-end doc-filter-row">
                        <Col lg={3} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.search')}</Label>
                                <div className="input-group">
                                    <Input
                                        type="text"
                                        innerRef={searchInputRef}
                                        placeholder={t('reglement.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
	                                            setFilters(prevFilters => ({
	                                                ...prevFilters,
	                                                query: value,
	                                                page: 0
	                                            }));
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch();
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup className="mb-0">
                                <Label htmlFor="clientId">{t('bonLivraison.client')}:</Label>
                                <Select
                                    id="clientId"
                                    options={clientOptionsWithAll}
                                    value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                                    onChange={(selectedOption) => {
	                                        setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null, page: 0 }));
                                        setSelectedClient(selectedOption);
                                    }}
                                    placeholder={t('bonLivraison.selectClient')}
                                    isSearchable
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        {/* <Col md={2}>
                            <FormGroup>
                                <Label for="modeReglement">
                                    <FaMoneyBillWave className="me-1" />
                                    {t('facture.modeReglement')} *
                                </Label>
                                <Input
                                    type="select"
                                    id="modeReglement"
                                    name="modeReglement"
                                    value={filters.modeReglement}
                                    onChange={(e) => setFilters(f => ({ ...f, modeReglement: e.target.value }))}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                    <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                    <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                    <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                    <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                </Input>
                            </FormGroup>
                        </Col> */}
                        <Col lg={2} md={1} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
	                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, page: 0 }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={1} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
	                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, page: 0 }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.statut')}</Label>
                                <Input
                                    type="select"
                                    value={filters.statut}
	                                    onChange={(e) => setFilters({ ...filters, statut: e.target.value, page: 0 })}
                                >
                                    <option value="">{t('reglement.allStatuts')}</option>
                                    <option value="EN_ATTENTE">{t('reglement.enAttente')}</option>
                                    <option value="ENCAISSE">{t('reglement.encaisse')}</option>
                                    <option value="CAUTION">{t('reglement.caution')}</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        {/* <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-3"
                                style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col> */}
                    </Row>
                </CardBody>
            </Card>
            <Card className="shadow-sm mt-4">
                <CardBody>
                    <Row>
                        <Col>

                            {loading ? (
                                <div className="text-center my-4"><Spinner color="primary" /></div>
                            ) : error ? (
                                <div className="text-danger">Erreur lors du chargement des réglements.</div>
	                            ) : (
	                                <>
		                                <DocumentPagination
		                                    pageNumber={reglementPageNumber}
		                                    totalPages={reglementTotalPages}
		                                    totalElements={safeList.length}
		                                    pageSize={reglementPageSize}
		                                    onPageChange={(page) => setFilters(f => ({ ...f, page }))}
		                                />
		                                <div className="doc-list">
		                                    {paginatedSafeList.length > 0 && (
		                                        <div className="doc-row doc-row-total" style={{ gridTemplateColumns: '48px minmax(220px, 1.1fr) minmax(220px, 1fr) minmax(210px, 1fr) minmax(220px, 1fr) auto' }}>
		                                            <div className="text-center">
		                                                <Input
		                                                    type="checkbox"
		                                                    onChange={(e) => {
		                                                        if (e.target.checked) {
		                                                            const reglementIds = paginatedSafeList
		                                                                .filter(reg => ['AVANCE', 'BON_LIVRAISON'].some(type => type === reg.typeReglement))
		                                                                .map(reg => reg.id);
		                                                            setCheckedReglements(reglementIds);
		                                                        } else {
		                                                            setCheckedReglements([]);
		                                                        }
		                                                    }}
		                                                    checked={
		                                                        checkedReglements.length > 0 &&
		                                                        checkedReglements.length === paginatedSafeList.filter(reg => ['AVANCE', 'BON_LIVRAISON'].some(type => type === reg.typeReglement)).length
		                                                    }
		                                                />
		                                            </div>
		                                            <div className="doc-row-main">
		                                                <span className="doc-title">{t('common.totals')}</span>
		                                                <span className="doc-subtitle">{paginatedSafeList.length} {t('reglement.reglements', 'reglements')}</span>
		                                            </div>
		                                            <div />
		                                            <div className="doc-amounts">
		                                                <div>
		                                                    <span>{t('reglement.montantRegle')}</span>
		                                                    <strong>{formatPrice(paginatedSafeList.reduce((sum, r) => sum + (r?.montantRegle || 0), 0))}</strong>
		                                                </div>
		                                            </div>
		                                            <div />
		                                            <div />
		                                        </div>
		                                    )}
		                                    {paginatedSafeList.map((reglement) => (
		                                        <div className="doc-row" key={reglement.id} style={{ gridTemplateColumns: '48px minmax(220px, 1.1fr) minmax(220px, 1fr) minmax(210px, 1fr) minmax(220px, 1fr) auto' }}>
		                                            <div className="text-center">
		                                                {!reglement.estFacture && ['AVANCE', 'BON_LIVRAISON'].some(type => type === reglement.typeReglement) && (
		                                                    <Input
		                                                        type="checkbox"
		                                                        checked={checkedReglements.includes(reglement.id)}
		                                                        onChange={() => handleCheckboxChange(reglement.id)}
		                                                    />
		                                                )}
		                                            </div>
		                                            <div className="doc-row-main">
		                                                <span className="doc-title">{reglement.reference || reglement.id}</span>
		                                                {reglement.referencePaiement && (
		                                                    <span className="doc-subtitle">{reglement.referencePaiement}</span>
		                                                )}
		                                                <span className="doc-subtitle">{t('reglement.dateReglement')}: {new Date(reglement.dateReglement).toLocaleDateString('fr-FR')}</span>
		                                            </div>
		                                            <div className="doc-client">
		                                                <strong>{reglement.nomClient || '-'}</strong>
		                                                <div className="d-flex flex-wrap gap-1">
		                                                    {reglement.estAvance || reglement.typeReglement === 'CAUTION' ? (
		                                                        <Badge color="info">N/A</Badge>
		                                                    ) : (
		                                                        <>
		                                                            {reglement.numFactures && reglement.numFactures.split(',').map((invoice, index) => (
		                                                                <Badge key={`invoice-${index}`} color="primary" style={{ fontSize: '10px' }}>
		                                                                    {invoice.trim()}
		                                                                </Badge>
		                                                            ))}
		                                                            {reglement.numBonLivraisons && reglement.numBonLivraisons.split(',').map((receipt, index) => (
		                                                                <Badge key={`receipt-${index}`} color="success" style={{ fontSize: '10px' }}>
		                                                                    {receipt.trim()}
		                                                                </Badge>
		                                                            ))}
		                                                        </>
		                                                    )}
		                                                </div>
		                                            </div>
		                                            <div className="doc-amounts">
		                                                <div>
		                                                    <span>{t('reglement.montantRegle')}</span>
		                                                    <strong>{formatPrice(reglement.montantRegle)}</strong>
		                                                </div>
		                                                {reglement.montantAffecte > 0 && (
		                                                    <div>
		                                                        <span>{t('reglement.affecte')}</span>
		                                                        <strong>{formatPrice(reglement.montantAffecte)}</strong>
		                                                    </div>
		                                                )}
		                                            </div>
		                                            <div className="doc-statuses">
		                                                {reglement.estAvance ? (
		                                                    <Badge color="warning"><FaMoneyBillWave className="me-1" />{t('reglement.avance')}</Badge>
		                                                ) : reglement.numFactures ? (
		                                                    <Badge color="primary"><FaFileInvoice className="me-1" />{t('reglement.factures')}</Badge>
		                                                ) : reglement.numBonLivraisons ? (
		                                                    <Badge color="success"><FaTruck className="me-1" />{t('reglement.bonLivraisons')}</Badge>
		                                                ) : reglement.typeReglement === 'CAUTION' ? (
		                                                    <Badge color="info"><FaMoneyBillWave className="me-1" />{t('reglement.caution')}</Badge>
		                                                ) : (
		                                                    <Badge color="secondary">{t('reglement.autre')}</Badge>
		                                                )}
		                                                {getStatutBadge(reglement.statut)}
		                                                <Badge color={reglement.estFacture ? 'success' : 'secondary'}>
		                                                    {t('reglement.estFacture')}: {reglement.estFacture ? t('common.yes', 'Oui') : t('common.no', 'Non')}
		                                                </Badge>
		                                                <span className="doc-subtitle">
		                                                    {getModeReglementIcon(reglement.modeReglement)} {t(`reglement.${reglement.modeReglement.toLowerCase()}`)}
		                                                </span>
		                                                {reglement.banque && (
		                                                    <span className="doc-subtitle">{reglement.banque} - {reglement.numeroCheque}</span>
		                                                )}
		                                                <span className="doc-subtitle">{t('reglement.dateEncaissement')}: {reglement.dateEncaissement ? new Date(reglement.dateEncaissement).toLocaleDateString('fr-FR') : '-'}</span>
		                                                <span className="doc-subtitle">{t('reglement.dateEcheance')}: {reglement.dateEcheance ? new Date(reglement.dateEcheance).toLocaleDateString('fr-FR') : '-'}</span>
		                                            </div>
		                                            <div className="doc-actions">
		                                                {(reglement.statut === 'EN_ATTENTE') && (
		                                                    <Button
		                                                        color="danger"
		                                                        title={t('reglement.annuler')}
		                                                        size="sm"
		                                                        className="px-2"
		                                                        onClick={() => handleAnnulerReglement(reglement)}
		                                                    >
		                                                        <FaTrashAlt size={12} /> Annuler
		                                                    </Button>
		                                                )}
		                                                {reglement.typeReglement != 'CAUTION' && reglement.statut === 'EN_ATTENTE' && (
		                                                    <Button
		                                                        color="success"
		                                                        title={t('reglement.encaisser')}
		                                                        onClick={() => handleEncaisserReglement(reglement)}
		                                                        size="sm"
		                                                        className="doc-icon-btn"
		                                                    >
		                                                        <FaCheckCircle size={12} />
		                                                    </Button>
		                                                )}
		                                                <Button
		                                                    color="warning"
		                                                    title={t('reglement.edit')}
		                                                    onClick={() => handleEdit(reglement)}
		                                                    size="sm"
		                                                    className="doc-icon-btn"
		                                                >
		                                                    <FaEdit size={12} />
		                                                </Button>
		                                                <Button
		                                                    color="secondary"
		                                                    title={t('reglement.generateReceipt') || 'GÃ©nÃ©rer un reÃ§u de paiement'}
		                                                    onClick={() => handleGenerateReceipt(reglement)}
		                                                    size="sm"
		                                                    className="doc-icon-btn"
		                                                >
		                                                    <FaReceipt size={12} />
		                                                </Button>
		                                            </div>
		                                        </div>
		                                    ))}
		                                    {safeList.length === 0 && (
		                                        <div className="doc-empty">{t('reglement.noReglements')}</div>
		                                    )}
		                                </div>
		                                <table className="table table-hover table-striped table-bordered d-none" style={{ fontSize: '0.8rem' }}>
                                    <thead className="thead-light">
                                        <tr>
                                            <th className="align-middle text-center" width="20">

                                                <div className="form-check">
                                                    <Input
                                                        type="checkbox"
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                // Only select deliveries that are in LIVRE status
	                                                                const reglementIds = paginatedSafeList
	                                                                    .filter(reg => ['AVANCE', 'BON_LIVRAISON'].some(type => type === reg.typeReglement))
	                                                                    .map(reg => reg.id);
                                                                setCheckedReglements(reglementIds);
                                                            } else {
                                                                setCheckedReglements([]);
                                                            }
                                                        }}
                                                        checked={
                                                            checkedReglements.length > 0 &&
	                                                            checkedReglements.length === paginatedSafeList.filter(reg => ['AVANCE', 'BON_LIVRAISON'].some(type => type === reg.typeReglement)).length
                                                        }
                                                    />
                                                </div>

                                            </th>
                                            <th>{t('reglement.reference')}</th>
                                            <th>{t('reglement.client')}</th>
                                            {/* <th>{t('reglement.chantier')}</th> */}
                                            <th>{t('reglement.documents')}</th>
                                            <th className="align-middle text-center">{t('reglement.type')}</th>
                                            <th>{t('reglement.dateReglement')}</th>
                                            <th>{t('reglement.montantRegle')}</th>
                                            <th>{t('reglement.modeReglement')}</th>
                                            <th>{t('reglement.statut')}</th>
                                            <th>{t('reglement.dateEncaissement')}</th>
                                            <th>{t('reglement.dateEcheance')}</th>
                                            <th>{t('reglement.estFacture')}</th>
                                            <th>{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
	                                        {paginatedSafeList.map((reglement) => {
                                            return (
                                                <tr key={reglement.id}>
                                                    <td className="align-middle text-center">
                                                        {!reglement.estFacture && ['AVANCE', 'BON_LIVRAISON'].some(type => type === reglement.typeReglement) && (
                                                            <Input
                                                                type="checkbox"
                                                                checked={checkedReglements.includes(reglement.id)}
                                                                onChange={() => handleCheckboxChange(reglement.id)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <strong>{reglement.reference || reglement.id}</strong>
                                                        {reglement.referencePaiement && (
                                                            <div className="text-muted small">
                                                                {reglement.referencePaiement}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {reglement.nomClient}
                                                    </td>
                                                    {/* <td>
                                                        {reglement.nomChantier || '-'}
                                                    </td> */}
                                                    <td className="align-middle text-center">
                                                        {reglement.estAvance || reglement.typeReglement === 'CAUTION' ? (
                                                            <div>
                                                                <Badge color="info" className="mb-1">
                                                                    N/A
                                                                </Badge>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                {reglement.numFactures && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        {reglement.numFactures.split(',').map((invoice, index) => (
                                                                            <Badge key={index} color="primary" className='mb-1' style={{ fontSize: '10px' }}>
                                                                                {invoice.trim()}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {reglement.numBonLivraisons && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        {reglement.numBonLivraisons.split(',').map((receipt, index) => (
                                                                            <Badge key={index} color="success" className='mb-1' style={{ fontSize: '10px' }}>
                                                                                {receipt.trim()}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )
                                                        }
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        {reglement.estAvance ? (
                                                            <Badge color="warning">
                                                                <FaMoneyBillWave className="me-1" />
                                                                {t('reglement.avance')}
                                                            </Badge>
                                                        ) : reglement.numFactures ? (
                                                            <Badge color="primary">
                                                                <FaFileInvoice className="me-1" />
                                                                {t('reglement.factures')}
                                                            </Badge>
                                                        ) : reglement.numBonLivraisons ? (
                                                            <Badge color="success">
                                                                <FaTruck className="me-1" />
                                                                {t('reglement.bonLivraisons')}
                                                            </Badge>
                                                        ) : reglement.typeReglement === 'CAUTION' ? (
                                                            <Badge color="info">
                                                                <FaMoneyBillWave className="me-1" />
                                                                {t('reglement.caution')}
                                                            </Badge>) : (
                                                            <Badge color="secondary">
                                                                {t('reglement.autre')}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {new Date(reglement.dateReglement).toLocaleDateString('fr-FR')}
                                                    </td>
                                                    <td className="text-end">
                                                        <strong>{formatPrice(reglement.montantRegle)}</strong>
                                                        {reglement.montantAffecte > 0 && (
                                                            <div className="text-muted small">
                                                                {t('reglement.affecte')}: {formatPrice(reglement.montantAffecte)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="me-1">
                                                            {getModeReglementIcon(reglement.modeReglement)}
                                                        </span>
                                                        {t(`reglement.${reglement.modeReglement.toLowerCase()}`)}
                                                        {reglement.banque && (
                                                            <div className="text-muted small">{reglement.banque}
                                                                <br />
                                                                {reglement.numeroCheque}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{getStatutBadge(reglement.statut)}</td>
                                                    <td>
                                                        {reglement.dateEncaissement
                                                            ? new Date(reglement.dateEncaissement).toLocaleDateString('fr-FR')
                                                            : '-'
                                                        }
                                                    </td>
                                                    <td>
                                                        {reglement.dateEcheance
                                                            ? new Date(reglement.dateEcheance).toLocaleDateString('fr-FR')
                                                            : '-'
                                                        }
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={reglement.estFacture}
                                                            readOnly
                                                            disabled
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="btn-group">
                                                            {/* LETTRAGE – visible seulement pour AVANCE */}
                                                            {(reglement.statut === 'EN_ATTENTE') && (
                                                                <Button
                                                                    color="danger"
                                                                    title={t('reglement.annuler')}
                                                                    size="sm"
                                                                    className="me-1"
                                                                    onClick={() => handleAnnulerReglement(reglement)}
                                                                >
                                                                    <FaTrashAlt size={12} />
                                                                </Button>
                                                            )}
                                                            {reglement.typeReglement != 'CAUTION' && reglement.statut === 'EN_ATTENTE' && (
                                                                <Button
                                                                    color="success"
                                                                    title={t('reglement.encaisser')}
                                                                    onClick={() => handleEncaisserReglement(reglement)}
                                                                    size="sm"
                                                                    className="me-1"
                                                                >
                                                                    <FaCheckCircle size={12} />
                                                                </Button>
                                                            )
                                                            }
                                                            {/* Bouton Éditer */}
                                                            <Button
                                                                color="warning"
                                                                title={t('reglement.edit')}
                                                                onClick={() => handleEdit(reglement)}
                                                                size="sm"
                                                                className="me-1"
                                                            //disabled={!canEditInvoice(invoice)}
                                                            >
                                                                <FaEdit size={12} />
                                                            </Button>
                                                            <Button
                                                                color="secondary"
                                                                title={t('reglement.generateReceipt') || 'Générer un reçu de paiement'}
                                                                onClick={() => handleGenerateReceipt(reglement)}
                                                                size="sm"
                                                                className="me-1"
                                                            >
                                                                <FaReceipt size={12} />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {safeList.length === 0 && (
                                            <tr>
                                                <td colSpan="14" className="text-center py-4">
                                                    <div className="text-muted">{t('reglement.noReglements')}</div>
                                                </td>
                                            </tr>)
                                        }
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light fw-bold">
                                            <td colSpan="7" className="text-end">{t('common.totals')}</td>
	                                            <td colSpan="2" >{formatPrice(paginatedSafeList.reduce((sum, r) => sum + (r?.montantRegle || 0), 0))}</td>
                                            <td colSpan="5"></td>
                                        </tr>
                                    </tfoot>
	                                </table>
	                                </>
	                            )}

                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Modal for Create/Edit */}
            <Modal
                isOpen={showModal}
                toggle={handleCloseModal}
                size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseModal}>
                    {selectedReglement
                        ? `${t('reglement.edit')} - ${selectedReglement.reference || selectedReglement.id}`
                        : t('reglement.new')
                    }
                </ModalHeader>
                <ModalBody>
                    <ReglementForm
                        reglement={selectedReglement}
                        onClose={handleCloseModal}
                    />
                </ModalBody>
            </Modal>

            {/* Invoice Creation Modal */}
            <Modal
                isOpen={showInvoiceModal}
                toggle={handleCloseInvoiceModal}
                size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseInvoiceModal}>
                    <div className="d-flex align-items-center">
                        <FaFileInvoice className="me-2 text-primary" />
                        {t('reglement.createInvoiceTitle')}
                    </div>
                </ModalHeader>
                <ModalBody>
                    {invoice && (
                        <div>
                            {/* Invoice Preview */}
                            <Card className="mb-4">
                                <CardBody>
                                    <Row>
                                        <Col md={2}>
                                            <Label><strong>{t('reglement.client')}:</strong></Label>
                                            <Input
                                                type="text"
                                                value={invoice.clientNom}
                                                disabled
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="invoiceDateFacture">
                                                    <strong>{t('reglement.dateFacture')}:</strong>
                                                </Label>
                                                <Input
                                                    type="date"
                                                    id="invoiceDateFacture"
                                                    value={invoiceDateFacture}
                                                    onChange={e => {
                                                        setInvoiceDateFacture(e.target.value);
                                                        setInvoice(prev => ({ ...prev, dateFacture: e.target.value }));
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="invoiceDateEcheance">
                                                    <strong>{t('reglement.dateEcheance')}:</strong>
                                                </Label>
                                                <Input
                                                    type="date"
                                                    id="invoiceDateEcheance"
                                                    value={invoiceDateEcheance}
                                                    onChange={e => {
                                                        setInvoiceDateEcheance(e.target.value);
                                                        setInvoice(prev => ({ ...prev, dateEcheance: e.target.value }));
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={2}>
                                            <Label>
                                                <strong>{t('reglement.bonLivraisonRefs')}:</strong>
                                            </Label>
                                            {invoice.bonLivraisonRefs && (
                                                <div >
                                                    <div >
                                                        {invoice.bonLivraisonRefs.split(',').map((ref, index) => (
                                                            <Badge key={index} color="success" className="me-1 mb-1">
                                                                {ref}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </Col>

                                        <Col md={3}>
                                            <FormGroup>
                                                <Label for="modeReglement">
                                                    <FaMoneyBillWave className="me-1" />
                                                    {t('facture.modeReglement')} *
                                                </Label>
                                                <Input
                                                    type="select"
                                                    id="modeReglement"
                                                    name="modeReglement"
                                                    value={invoice.modeReglement}
                                                    onChange={(e) => setInvoice({ ...invoice, modeReglement: e.target.value })}
                                                >
                                                    <option value="">{t('common.select')}</option>
                                                    <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                                    <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                                    <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                                    <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                                    <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {/* Selected Reglements Section */}
                                    {invoice.selectedReglements && invoice.selectedReglements.length > 0 && (
                                        <div className="mt-4">
                                            <h6>
                                                <FaMoneyBillWave className="me-2 text-primary" />
                                                {t('reglement.selectedReglements')}
                                            </h6>
                                            <Table striped bordered size="sm" className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t('reglement.reference')}</th>
                                                        <th>{t('reglement.type')}</th>
                                                        <th>{t('reglement.dateReglement')}</th>
                                                        <th>{t('reglement.modeReglement')}</th>
                                                        <th className="text-end">{t('reglement.montantRegle')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoice.selectedReglements.map((reglement, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <strong>{reglement.reference}</strong>
                                                            </td>
                                                            <td>
                                                                <Badge
                                                                    color={reglement.typeReglement === 'AVANCE' ? 'warning' : 'success'}
                                                                    className="d-flex align-items-center justify-content-center"
                                                                    style={{ width: 'fit-content' }}
                                                                >
                                                                    {reglement.typeReglement === 'AVANCE' ? (
                                                                        <><FaMoneyBillWave className="me-1" size="10" /> {t('reglement.avance')}</>
                                                                    ) : (
                                                                        <><FaTruck className="me-1" size="10" /> {t('reglement.bonLivraisons')}</>
                                                                    )}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                {new Date(reglement.dateReglement).toLocaleDateString('fr-FR')}
                                                            </td>
                                                            <td>
                                                                <span className="me-1">
                                                                    {getModeReglementIcon(reglement.modeReglement)}
                                                                </span>
                                                                {t(`reglement.${reglement.modeReglement.toLowerCase()}`)}
                                                            </td>
                                                            <td className="text-end">
                                                                <strong>{formatPrice(reglement.montantRegle)}</strong>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="4" className="text-end fw-bold">
                                                            {t('common.totals')}:
                                                        </td>
                                                        <td className="text-end fw-bold">
                                                            {formatPrice(invoice.selectedReglements.reduce((total, reg) => total + reg.montantRegle, 0))}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </Table>
                                        </div>
                                    )}

                                    {/* Editable Invoice Lines Section */}
                                    <div className="mt-4">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6>{t('reglement.invoiceLines')}</h6>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={toggleChooseProductModal}
                                            >
                                                <FaPlus className="me-1" />
                                                {t('reglement.addLine')}
                                            </Button>
                                        </div>

                                        {invoice.lignes && invoice.lignes.length > 0 ? (
                                            <Table striped bordered size="sm" responsive>
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '20%' }}>{t('reglement.article')}</th>
                                                        <th style={{ width: '15%' }}>{t('reglement.quantite')}</th>
                                                        <th style={{ width: '10%' }}>{t('reglement.unite')}</th>
                                                        <th style={{ width: '15%' }}>{t('reglement.prixUnitaireTTC')}</th>
                                                        <th style={{ width: '10%' }}>{t('reglement.tauxTVA')}</th>
                                                        <th style={{ width: '15%' }}>{t('reglement.montantHT')}</th>
                                                        <th style={{ width: '15%' }}>{t('reglement.montantTTC')}</th>
                                                        <th style={{ width: '5%' }}>{t('common.actions')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoice.lignes.map((line, index) => (
                                                        <tr key={index}>
                                                            <td>

                                                                <Input
                                                                    type="text"
                                                                    size="sm"
                                                                    value={line.articleNom}
                                                                    disabled
                                                                    onChange={e => updateInvoiceLine(index, 'articleNom', e.target.value)}
                                                                    placeholder={t('reglement.articleName')}
                                                                />
                                                            </td>
                                                            <td>

                                                                <Input
                                                                    type="number"
                                                                    size="sm"
                                                                    lang="en"
                                                                    onFocus={(e) => e.target.select()}
                                                                    name="quantite"
                                                                    value={line.quantite}
                                                                    onChange={e => updateInvoiceLine(index, 'quantite', e.target.value || 0)}
                                                                    min="0"
                                                                    step="any"
                                                                />

                                                            </td>
                                                            <td>
                                                                <Input
                                                                    type="text"
                                                                    size="sm"
                                                                    disabled
                                                                    value={line.unite}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Input
                                                                    type="number"
                                                                    size="sm"
                                                                    lang="en"
                                                                    onFocus={(e) => e.target.select()}
                                                                    value={line.prixUnitaireTTC}
                                                                    onChange={e => updateInvoiceLine(index, 'prixUnitaireTTC', e.target.value || 0)}
                                                                    min="0"
                                                                    step="any"
                                                                />
                                                            </td>
                                                            <td>
                                                                <small>{Number(line.tauxTVA * 100).toFixed(2)}%</small>
                                                            </td>
                                                            <td className="text-end">
                                                                {formatPrice(line.montantHT, false)}
                                                            </td>
                                                            <td className="text-end">
                                                                <strong>{formatPrice(line.montantTTC, false)}</strong>
                                                            </td>
                                                            <td className="text-center">
                                                                <Button
                                                                    color="danger"
                                                                    size="sm"
                                                                    onClick={() => removeInvoiceLine(index)}
                                                                    title={t('reglement.removeLine')}
                                                                >
                                                                    <FaTimes size={10} />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="5" className="text-end fw-bold">
                                                            {t('common.totals')}:
                                                        </td>
                                                        <td className="text-end fw-bold">
                                                            {formatPrice(invoice.lignes.reduce((sum, line) => sum + (line.montantHT || 0), 0), false)}
                                                        </td>
                                                        <td className="text-end fw-bold">
                                                            {formatPrice(invoice.lignes.reduce((sum, line) => sum + (line.montantTTC || 0), 0), false)}
                                                        </td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>
                                            </Table>
                                        ) : (
                                            <Alert color="warning" className="text-center">
                                                <FaInfoCircle className="me-2" />
                                                {t('reglement.noLinesYet')}
                                                <Button
                                                    color="link"
                                                    className="p-0 ms-2"
                                                    onClick={toggleChooseProductModal}
                                                >
                                                    {t('reglement.addFirstLine')}
                                                </Button>
                                            </Alert>
                                        )}

                                        {/* Display target vs current total */}
                                        {invoice.targetMontantTTC && (
                                            <Alert
                                                color={Math.abs((invoice.lignes.reduce((sum, line) => sum + (line.montantTTC || 0), 0)) - invoice.targetMontantTTC) < 0.01 ? 'success' : 'warning'}
                                                className="mt-3"
                                            >
                                                <div className="d-flex justify-content-between">
                                                    <span><strong>{t('reglement.targetTotal')}:</strong> {formatPrice(invoice.targetMontantTTC)}</span>
                                                    <span><strong>{t('reglement.currentTotal')}:</strong> {formatPrice(invoice.lignes.reduce((sum, line) => sum + (line.montantTTC || 0), 0))}</span>
                                                    <span><strong>{t('reglement.difference')}:</strong> {formatPrice((invoice.lignes.reduce((sum, line) => sum + (line.montantTTC || 0), 0)) - invoice.targetMontantTTC)}</span>
                                                </div>
                                            </Alert>
                                        )}
                                    </div>

                                    <div className="mt-4">
                                        <h5 className="text-end">
                                            <p><strong>{t('cart.totalHT')}:</strong> {formatPrice(invoice.lignes.reduce((sum, line) => sum + (line.montantHT || 0), 0))}</p>
                                            <p><strong>{t('cart.tva')}:</strong> {formatPrice(invoice.lignes.reduce((sum, line) => sum + (line.montantTVA || 0), 0))}</p>
                                            <p><strong>{t('cart.totalTTC')}:</strong> {formatPrice(invoice.lignes.reduce((sum, line) => sum + (line.montantTTC || 0), 0))}</p>
                                        </h5>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="success"
                        onClick={handleConfirmCreateInvoice}
                        disabled={!invoice}
                    >
                        <FaFileInvoice className="me-2" />
                        {t('reglement.confirmCreateInvoice')}
                    </Button>
                    <Button
                        color="secondary"
                        onClick={handleCloseInvoiceModal}
                    >
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={chooseProductModal} toggle={toggleChooseProductModal} size="xl">
                <ModalHeader toggle={toggleChooseProductModal}>
                    {t('product.chooseProduct')}
                </ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md={12}>
                            <Input
                                type="text"
                                placeholder={t('product.searchPlaceholder')}
                                value={modalSearchTerm}
                                onChange={handleModalSearchChange}
                                className="me-2" // Added margin to the right
                            />
                        </Col>

                    </Row>
                    <DisplayToggle />
                    {displayMode === 'table' ? <TableView /> : <CardView />}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => {
                        setModalSearchTerm('');
                        setCurrentPage(1);
                        toggleChooseProductModal()
                    }}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>

        </Container >
    );
};

export default ReglementsList;


